import {
  ContractHistoryActivityPeriodFragment,
  ContractHistoryContractVersionHistoryFragment,
  ContractVersionPageContractDataFragment,
  ContractsValueObjectsContractScheduleEnum,
} from 'generated/resources/graphql'
import minBy from 'lodash/minBy'
import orderBy from 'lodash/orderBy'
import moment, { Moment } from 'moment'

import { stringToDate } from 'modules/core/lib/dates'

import type { Contract } from 'modules/contracts/components/NewContractHistory/types'

type OrderType = 'desc' | 'asc'

class ContractVersionContract {
  startsOn: Moment
  endsOn: Moment | null
  schedule: ContractsValueObjectsContractScheduleEnum
  versions: ContractHistoryContractVersionHistoryFragment[]
  activityPeriods: ContractHistoryActivityPeriodFragment[]

  constructor(startsOn: string | Moment, schedule: ContractsValueObjectsContractScheduleEnum) {
    this.startsOn = moment(startsOn)
    this.schedule = schedule
    this.endsOn = null
    this.versions = []
    this.activityPeriods = []
  }

  static fromContract(
    contract: Contract | ContractVersionPageContractDataFragment
  ): ContractVersionContract {
    const instance = new ContractVersionContract(contract.startsOn, contract.schedule)

    if (contract.endsOn) {
      instance.setEndDate(contract.endsOn)
    }

    if ('contractVersions' in contract && contract.contractVersions) {
      contract.contractVersions.forEach(
        (version: ContractHistoryContractVersionHistoryFragment) => {
          instance.pushVersion(version)
        }
      )
    }

    if (contract.activityPeriods) {
      contract.activityPeriods.forEach((activityPeriod) => {
        instance.pushActivityPeriod(activityPeriod)
      })
    }

    return instance
  }

  sortedVersions(order: OrderType = 'desc'): ContractHistoryContractVersionHistoryFragment[] {
    return orderBy(this.versions, ['effectiveOn'], [order])
  }

  effectiveOn(): Moment | null {
    const minEffectiveOnVersion = minBy(this.versions, 'effectiveOn')
    if (!minEffectiveOnVersion?.effectiveOn) return null

    const effectiveOnDate = stringToDate(minEffectiveOnVersion.effectiveOn, 'backend')
    if (!effectiveOnDate || !effectiveOnDate.isValid()) return null

    return effectiveOnDate
  }

  pushVersion(version: ContractHistoryContractVersionHistoryFragment): void {
    this.versions.push(version)
  }

  pushActivityPeriod(activityPeriod: ContractHistoryActivityPeriodFragment): void {
    this.activityPeriods.push(activityPeriod)
  }

  formattedStartOn(format = 'MMM Do YYYY'): string {
    return this.startsOn.format(format)
  }

  hasEndDate(): boolean {
    return this.endsOn !== null
  }

  getLatestVersion(): ContractHistoryContractVersionHistoryFragment | undefined {
    return this.sortedVersions('desc')[0]
  }

  isFirstVersion(version: ContractHistoryContractVersionHistoryFragment): boolean {
    const firstVersion = this.sortedVersions('asc')[0]
    return firstVersion?.id === version.id
  }

  hasMultipleVersions(): boolean {
    return this.versions.length > 1
  }

  setEndDate(endDate: string | Moment | null): void {
    this.endsOn = endDate ? moment(endDate) : null
  }

  getLatestOngoingPeriod(): ContractHistoryActivityPeriodFragment | undefined {
    return this.activityPeriods.find((period) => period.schedule === 'ongoing')
  }

  hasMultipleActivityPeriods(): boolean {
    return this.activityPeriods.length > 1
  }
}

export default ContractVersionContract
